import React, { useState, useMemo } from 'react';

/**
 * Avatar component.
 * Accepts either:
 * - `avatarUrl`: relative API URL (e.g. "/api/files/xxx") - will be resolved with backend URL + auth token
 * - `src`: full URL (used as-is)
 * Falls back to Golden TV logo, then to monogram.
 */
export default function Avatar({ username = '', src, avatarUrl, size = 40, className = '' }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const [srcFailed, setSrcFailed] = useState(false);
  const initial = (username?.[0] || '?').toUpperCase();

  const resolvedSrc = useMemo(() => {
    if (src) return src;
    if (avatarUrl) {
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('gtc_token');
      return `${base}${avatarUrl}?auth=${token}`;
    }
    return null;
  }, [src, avatarUrl]);

  const displaySrc = resolvedSrc && !srcFailed ? resolvedSrc : (logoFailed ? null : '/logo.png');
  const style = { width: size, height: size };

  if (displaySrc) {
    return (
      <img
        src={displaySrc}
        alt={username}
        onError={() => {
          if (resolvedSrc && !srcFailed) setSrcFailed(true);
          else setLogoFailed(true);
        }}
        style={style}
        className={`rounded-full object-cover border border-[var(--gold)]/30 bg-[var(--bg-elevated)] ${className}`}
        data-testid="user-avatar"
      />
    );
  }

  return (
    <div
      style={style}
      className={`rounded-full bg-[var(--gold-soft)] border border-[var(--gold)]/30 flex items-center justify-center text-[var(--gold)] font-heading font-semibold ${className}`}
      data-testid="user-avatar-fallback"
    >
      {initial}
    </div>
  );
}
